import {createAsyncThunk} from '@reduxjs/toolkit'

import {APIStatus, APIStatusType, ElementId, ShoppingLiveStorageKey} from '@web/core/constants'
import {SHOPPING_LIVE, SHOPPING_LIVE_LAUNCHER} from '@web/core/urls'
import {loadExternalScript} from '@web/helpers/browser'
import getStorage from '@web/helpers/storage'
import {resultSelector, ResultState, ShoppingLive} from '@web/slices/result/slice'

import {ShoppingLiveData} from './interfaces'

/**
 * C2 useCheckoutInit 훅에서 saveStorage와 같은 시점에 호출
 * 주문서 조회 시 쇼핑라이브 정보가 있는경우 Storage 생성
 */
export const initShoppingLiveStorage = createAsyncThunk<string, Maybe<ShoppingLiveData>>(
    'shoppingLive/initShoppingLiveStorage',
    async (shoppingData) => {
        const storage = getStorage<ShoppingLiveData>(ShoppingLiveStorageKey)
        let message
        if (shoppingData) {
            storage.set(shoppingData)
            message = '쇼핑라이브 storage를 생성했습니다.'
        } else {
            storage.remove()
            message = '쇼핑라이브 정보가 없어 storage를 clear 했습니다.'
        }
        return message
    },
)

/**
 * 주문서 > FDS > 승인페이지 > 주문완료
 * 승인페이지에서 orderNo을 key로 하는 Storage로 쇼핑라이브 정보를 옮겨 관리
 */
export const switchShoppingLiveStorage = createAsyncThunk<string, string, object>(
    'shoppingLive/switchShoppingLiveStorage',
    async (orderNo) => {
        const originStorage = getStorage<ShoppingLiveData>(ShoppingLiveStorageKey)
        if (originStorage.exists()) {
            /** 새로운 주문을 하기 전까지는 주문완료에서 쇼핑라이브 컨텐츠를 볼 수 있도록 clear는 다음 조회시점에 수행 */
            const switchStorage = getStorage<ShoppingLiveData>(orderNo)
            switchStorage.set(originStorage.get(null))

            return '주문완료번호를 key로 갖는 쇼핑라이브 storage를 생성했습니다.'
        } else {
            return '쇼핑라이브 storage가 없습니다.'
        }
    },
)

/**
 * 주문완료 페이지 접근 시 쇼핑라이브 정보를 가져옵니다.
 */
export const getShoppingLiveStorage = createAsyncThunk<
    {
        shoppingData: Maybe<ShoppingLiveData>
        message: string
    },
    string
>('shoppingLive/getShoppingLiveStorage', async (orderNo) => {
    const storage = getStorage<ShoppingLiveData>(orderNo)

    if (storage.exists()) {
        return {
            shoppingData: storage.get(null),
            message: '쇼핑라이브 Storage로부터 정보를 가져왔습니다.',
        }
    } else {
        return {
            shoppingData: null,
            message: '쇼핑라이브 Storage가 없습니다.',
        }
    }
})

/**
 * C2 쇼핑라이브 진입 주문건
 * 쇼핑라이브 라이브러리 컨텐츠 노출
 * */
export const loadShoppingLiveContent = createAsyncThunk<
    {
        status: APIStatusType
        message: string
    },
    {
        shoppingLive: ShoppingLive
        setLoadStatus: React.Dispatch<React.SetStateAction<APIStatusType>>
    },
    {
        state: {
            result: ResultState
        }
    }
>('shoppingLive/loadShoppingLiveContent', ({shoppingLive, setLoadStatus}, {getState}) => {
    const state = getState()
    const {
        purchaser: {naverId, naverIdNo},
        order: {orderNo},
    } = resultSelector(state)

    return new Promise((resolve) => {
        const moduleConfig = {
            orderId: orderNo,
            naverId,
            userId: naverIdNo,
            extras: encodeURI(shoppingLive.extras ?? ''),
            cartFrom: shoppingLive.cartFrom,
            prdFrom: shoppingLive.prdFrom,
            target: document.getElementById(ElementId.SHOPPING_LIVE),
        }

        const onLoadScript = () => {
            window['lico']?.launcher({
                name: 'NPayOrderContent',
                module: {
                    resourcePath: SHOPPING_LIVE,
                    js: 'npay.order.content.js',
                    css: 'npay.order.content.css',
                },
                moduleConfig,
                onLoad: () => {
                    setLoadStatus(APIStatus.RESOLVED)
                    resolve({
                        status: APIStatus.RESOLVED,
                        message: '쇼핑라이브 Module로부터 컨텐츠를 성공적으로 가져왔습니다.',
                    })
                },
                onError: () => {
                    setLoadStatus(APIStatus.REJECTED)
                    resolve({
                        status: APIStatus.REJECTED,
                        message: '쇼핑라이브 Module로부터 컨텐츠를 받아오지 못했습니다.',
                    })
                },
            })
        }

        loadExternalScript({sourceAddress: SHOPPING_LIVE_LAUNCHER, onLoad: onLoadScript})
    })
})
